import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ContentLoader,
  List,
  Modal,
  Notification,
  SearchForm,
  Checkbox,
  ConfirmationPopup,
} from 'shared/components';
import { displayValue, formatDate, formatOrderBy } from 'shared/helpers';
import { searchType } from 'shared/constants';
import api from 'shared/api';
import { removeAccountOrders } from '../../../actions';

const PAGE_SIZE = 20;

const getOrderIdentifier = (order) => get(order, 'id');

const RemoveOrdersModal = ({ accountId, closeCb, confirmCb }) => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(1);
  const [activeQuery, setActiveQuery] = useState('');
  const [searchBy, setSearchBy] = useState(searchType.order_storeID);
  const [currentSort, setCurrentSort] = useState([
    { desc: true, id: 'created_at' },
  ]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [isSubmitting, setSubmitting] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);

  const fetchOrders = useCallback(() => {
    if (!accountId) {
      setOrders([]);
      setPages(1);
      return;
    }

    setLoading(true);
    setError(false);

    const queryUrl = activeQuery ? `&${searchBy}=${activeQuery}` : '';
    const sortUrl = formatOrderBy(currentSort);
    const url = `/api/v1/orders/?limit=${PAGE_SIZE}&offset=${
      page * PAGE_SIZE
    }&customer__account=${accountId}${queryUrl}${sortUrl}`;

    api
      .get(url)
      .then((res) => {
        const results = get(res, 'data.results', []);
        const count = get(res, 'data.count', results.length);
        setOrders(results);
        setPages(Math.max(1, Math.ceil(count / PAGE_SIZE)));
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setOrders([]);
        setPages(1);
        setLoading(false);
      });
  }, [accountId, activeQuery, currentSort, page, searchBy]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const toggleOrderSelection = (order) => {
    const orderId = getOrderIdentifier(order);
    if (orderId === undefined || orderId === null) {
      return;
    }
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : prev.concat(orderId),
    );
  };

  const handleSubmit = () => {
    if (isSubmitting || !selectedOrders.length) {
      return false;
    }
    setSubmitting(true);
    return removeAccountOrders(selectedOrders)
      .then(() => {
        setSubmitting(false);
        setShowConfirmPopup(false);
        Notification('success', t('Changes saved successfully'));
        confirmCb();
        return true;
      })
      .catch(() => {
        setSubmitting(false);
        Notification(
          'error',
          t('Your changes were not saved'),
          t('There was an error while saving your changes'),
        );
        return false;
      });
  };

  const handleConfirmOpen = () => {
    if (isSubmitting || !selectedOrders.length) {
      return false;
    }
    setShowConfirmPopup(true);
    return true;
  };

  const handleSearchSubmit = (value) => {
    setActiveQuery(value);
    setPage(0);
  };

  const handleClear = () => {
    setActiveQuery('');
    setPage(0);
  };

  const handleSearchTypeChange = (newType) => {
    setSearchBy(newType);
    setPage(0);
  };

  const handleSortChange = (newSorted) => {
    setCurrentSort(newSorted);
    setPage(0);
  };

  const handleRetry = () => fetchOrders();

  const columns = [
    {
      id: 'select',
      className: 'select-container',
      Cell: (rowData) => {
        const orderId = getOrderIdentifier(rowData.original);
        const isSelected = selectedOrders.includes(orderId);
        return (
          <button
            className="table-select"
            aria-label={t('Select order')}
            onClick={(event) => {
              event.stopPropagation();
              toggleOrderSelection(rowData.original);
            }}
            type="button"
          >
            <Checkbox
              inputId={`remove-order-${orderId || rowData.index}-select`}
              handleChange={() => toggleOrderSelection(rowData.original)}
              checked={isSelected}
              onClick={(event) => event.stopPropagation()}
            />
          </button>
        );
      },
      width: 60,
      show: true,
      sortable: false,
    },
    {
      Header: t('Created on'),
      accessor: 'created_at',
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 180,
    },
    {
      Header: t('Order ID'),
      accessor: 'store_id',
      Cell: (cellData) => displayValue(cellData.value),
    },
  ];

  return (
    <Modal
      closeCb={closeCb}
      confirmCb={handleConfirmOpen}
      disabled={isSubmitting || !selectedOrders.length}
      title={t('Remove orders')}
      size="lg"
    >
      <div className="CustomerAccountOrdersModal">
        <div className="list-header">
          <SearchForm
            options={[
              {
                label: t('Search by order id'),
                value: searchType.order_storeID,
              },
            ]}
            selectedValue={searchBy}
            handleSearchTypeChange={handleSearchTypeChange}
            handleSearchSubmit={handleSearchSubmit}
            activeQuery={activeQuery}
            handleClear={handleClear}
            disabled={loading}
          />
          <div />
        </div>
        {loading ? (
          <ContentLoader text={t('Loading orders')} />
        ) : error ? (
          <div className="CustomerAccount-error">
            <p>{t('Unable to load orders')}</p>
            <Button theme="info" size="sm" onClick={handleRetry}>
              {t('Retry')}
            </Button>
          </div>
        ) : orders.length ? (
          <List
            data={orders}
            columns={columns}
            loading={loading}
            manual
            page={page}
            pages={pages}
            minRows={orders.length || 5}
            showPagination={pages > 1}
            defaultPageSize={PAGE_SIZE}
            onPageChange={setPage}
            onSortedChange={handleSortChange}
            defaultSorted={currentSort}
          />
        ) : (
          <div className="CustomerAccount-empty">
            {t('No orders to remove')}
          </div>
        )}
      </div>
      {showConfirmPopup && (
        <ConfirmationPopup
          title={t('Remove orders')}
          confirmText={t('Remove')}
          theme="error"
          warning
          confirmCb={handleSubmit}
          closeCb={() => setShowConfirmPopup(false)}
          disabled={isSubmitting}
        >
          <p>{t('Are you sure you want to remove the selected orders?')}</p>
          <p>{t('You may not be able to see these orders anymore.')}</p>
        </ConfirmationPopup>
      )}
    </Modal>
  );
};

RemoveOrdersModal.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  closeCb: PropTypes.func.isRequired,
  confirmCb: PropTypes.func.isRequired,
};

export default RemoveOrdersModal;
